import { StockItemService } from 'src/app/acumatica/EntityServices/StockItem.service';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { Component, ElementRef, HostListener, OnInit, ViewChild, isDevMode } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BinderSpecification, SampleBooksService } from 'src/app/acumatica/EntityServices/SampleBooks.service';
import { KitStockComponent, KitStockComponentTypeEnum } from 'src/app/acumatica/EntityServices/KitSpecification.service';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { Sound } from 'src/app/services/audio.service';
import { MayerFabricsService } from 'src/app/services/mayer-fabrics.service';
import { ApplicationScreen, ScreenContextMenuItem } from 'src/app/application-navigator/application-screen';
import { NotFoundError } from 'src/app/acumatica/acumatica-errors';
import { FeedbackSnackbarComponent } from 'src/app/common/feedback-snackbar/feedback-snackbar.component';

@Component({
  selector: 'app-card-audit',
  templateUrl: './card-audit.component.html',
  styleUrls: ['./card-audit.component.css']
})
export class CardAuditComponent extends ApplicationScreen implements OnInit {
  binder?: BinderSpecification;
  binderCards?: KitStockComponent[];
  pageTitle = new FormControl('Binder Card Audit');
  @ViewChild('inventoryID') inventoryIDInputField!: ElementRef;

  cardScanInputForm: FormGroup = new FormGroup({
    InventoryID: new FormControl(null)
  });

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public binders: SampleBooksService,
    private MayerFabrics: MayerFabricsService,
    private stockItems: StockItemService,
    private snackBar: MatSnackBar,
  ) { super(); }

  screenMenuItems: ScreenContextMenuItem[] = [
    {
      label: 'Mark All',
      icon: 'check',
      handler: () => {
        this.binderCards?.forEach(card => this.binders.AuditCard(card, true));
        this.binder!.UserAudited = true;
      },
      condition: isDevMode()
    },
  ]

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      let inventoryID = params.get('inventoryID');
      if (!inventoryID) { this.gotoBinderList(); return; }
      let binder = this.binders.BinderShelf[inventoryID];
      if (binder === undefined) { this.gotoBinderList(); return; }

      this.binder = binder;
      this.binderCards = binder.StockComponents.filter(component => component.componentType === KitStockComponentTypeEnum.Card);

      setTimeout(() => {
        this.pageTitle.setValue('Audit ' + binder.Description);
      });
    });
  }

  gotoBinderList() { this.router.navigate(['/binder-audit']); }

  // #region UI template exposures
  LifeCycleColor = this.MayerFabrics.LifeCycleColor;
  LifeCycleContrastColor = this.MayerFabrics.LifeCycleContrastColor;
  // #endregion

  // #region binder audit
  resetScannerInput() {
    let inputControl = this.cardScanInputForm.get('InventoryID')!;
    inputControl.setValue(null);
    (<any>inputControl).focus();
  }

  toggleAuditCheck(card: KitStockComponent, event: MouseEvent) {
    this.binders.AuditCard(card, !this.binders.AuditStatus(card));
    this.binder!.UserAudited = true;
    event.stopPropagation();
  }

  lastScanRef?: MatSnackBarRef<TextOnlySnackBar>;
  cardScanInputFormSubmit() {
    let inputControl = this.cardScanInputForm.get('InventoryID')!;
    let inventoryID: string = (inputControl.value || '').toUpperCase().trim();

    if (inventoryID.trim().toUpperCase() == 'WC920') { inventoryID = 'WC920CD'; } // Editor card printed without CD
    if (inventoryID.trim().toUpperCase() == '336D') { inventoryID = '336CD'; } // Saxony card printed without CD

    let searchResult = this.binders.FindContainingBinder(inventoryID);

    // not found in any binder
    if (searchResult == null) {
      this.dialog.open(AlertDialog, {
        data: {
          sound: Sound.ErrorBeep,
          message: "\"" + inventoryID + "\" was not found in any current Binders",
          icon: 'warning',
          buttonText: 'Ok'
        },
      }).afterClosed().subscribe(() => {
        this.resetScannerInput();
        snackBarRef.dismiss();
      });

      let snackBarRef = this.snackBar.openFromComponent(FeedbackSnackbarComponent, { data: { message: "Getting item information..." } });

      this.stockItems.get(inventoryID, { loader: null }).subscribe(item => {
        snackBarRef.instance.message = item.Description + "\nLife Cycle: " + item.LifeCycle;
      }, err => {
        if (snackBarRef === undefined) return;

        if (err instanceof NotFoundError) {
          snackBarRef.instance.message = "Invalid Inventory ID / Nonexistent item";
        } else {
          throw err;
        }
      });

      return;
    }

    // was it already scanned in?
    if (this.binders.AuditStatus(searchResult.Component)) {
      this.dialog.open(AlertDialog, {
        data: {
          sound: Sound.GentleBeep,
          message:
            inventoryID + " - " + searchResult.Component.Description + "\nwas already found for binder\n" + searchResult.Binder.Description + "\n\nDuplicate card?",
          icon: 'file_copy',
          buttonText: 'Ok'
        },
      }).afterClosed().subscribe(() => this.resetScannerInput());
      return;
    }

    this.binders.AuditCard(searchResult.Component);

    // found but in a different binder
    if (searchResult.Binder != this.binder) {
      this.dialog.open(AlertDialog, {
        data: {
          sound: Sound.GentleBeep,
          message:
            inventoryID + " - " + searchResult.Component.Description + "\nbelongs in " + searchResult.Binder.Description,
          icon: 'exit_to_app',
          buttonText: 'Ok'
        },
      }).afterClosed().subscribe(() => this.resetScannerInput());
      return;
    }

    // valid scan input
    this.binder!.UserAudited = true;
    this.resetScannerInput();

    if (this.lastScanRef) this.lastScanRef.dismiss();
    this.lastScanRef = this.snackBar.open(searchResult.Component.InventoryID + " - " + searchResult.Component.Description, undefined, {
      duration: 2000
    });
  }

  // #endregion

  _focusIsInInput: boolean = false;
  input_focus() { this._focusIsInInput = true; }
  input_blur() { this._focusIsInInput = false; }

  @HostListener('document:keydown', ['$event'])
  document_keydown(event: KeyboardEvent) {
    if (this._focusIsInInput) return;
    if (event.altKey || event.ctrlKey) return;

    (<any>this.inventoryIDInputField.nativeElement).focus();
  }
}